// TacticalRenderer.js — mil-sym-ts WebRenderer adapter for multipoint tactical graphics.
// Converts coordinates + SIDC → GeoJSON FeatureCollection via WebRenderer.RenderSymbol2D.

import { WebRenderer, MilStdAttributes } from '@armyc2.c5isr.renderer/mil-sym-ts-web';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Convert a GeoJSON coordinates array ([[lng,lat], …]) to the mil-sym-ts
 * control-points string format: "lng1,lat1 lng2,lat2 …"
 */
function coordsToControlPoints(coords) {
    return coords.map(([x, y]) => `${x},${y}`).join(' ');
}

/**
 * Derive a bounding-box string "minX,minY,maxX,maxY" from an array of [lng,lat] pairs.
 */
function coordsToBbox(coords) {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const [x, y] of coords) {
        if (x < minX) minX = x; if (x > maxX) maxX = x;
        if (y < minY) minY = y; if (y > maxY) maxY = y;
    }
    return `${minX},${minY},${maxX},${maxY}`;
}

/** Build a Modifiers Map from a plain object using Modifiers.* keys. */
function buildModifiersMap(mods) {
    const m = new Map();
    if (!mods) return m;
    for (const [k, v] of Object.entries(mods)) {
        if (v != null && v !== '') m.set(k, String(v));
    }
    return m;
}

/** Build a MilStdAttributes Map with sensible defaults. */
function buildAttributesMap(opts = {}) {
    const a = new Map();
    a.set(MilStdAttributes.LineWidth, String(opts.lineWidth ?? 3));
    a.set(MilStdAttributes.UseDashArray, 'true');
    return a;
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Render a multipoint tactical graphic to a GeoJSON FeatureCollection string.
 *
 * @param {string} sidc          20-digit MIL-STD-2525D/E SIDC
 * @param {Array}  coordinates   Array of [lng, lat] pairs
 * @param {object} modifiers     Plain object keyed by Modifiers.* constants
 * @param {object} opts          { lineWidth?, pixelWidth?, pixelHeight?, viewportBbox? }
 *   pixelWidth/pixelHeight should be the visible map area in CSS px (map.getSize()).
 *   viewportBbox should be the current map viewport extent as "minLng,minLat,maxLng,maxLat".
 *   Both fall back to 800×600 / tight symbol bbox when no live map is available.
 * @returns {object|null}        Parsed GeoJSON FeatureCollection, or null on error
 */
export function renderTacticalGraphic(sidc, coordinates, modifiers = {}, opts = {}) {
    if (!coordinates?.length) return null;

    const controlPoints = coordsToControlPoints(coordinates);
    // ponytail: use viewport bbox per API spec; tight bbox only as last-resort fallback.
    const bbox = opts.viewportBbox ?? coordsToBbox(coordinates);
    const pw = opts.pixelWidth ?? 800;
    const ph = opts.pixelHeight ?? 600;
    const modsMap = buildModifiersMap(modifiers);
    const attrsMap = buildAttributesMap(opts);

    try {
        const json = WebRenderer.RenderSymbol2D(
            sidc,           // id (reuse sidc as unique id)
            sidc,           // name
            '',             // description
            sidc,           // symbolCode
            controlPoints,
            pw, ph,
            bbox,
            modsMap,
            attrsMap,
            WebRenderer.OUTPUT_FORMAT_GEOJSON,
        );
        if (!json) return null;
        return JSON.parse(json);
    } catch (_) {
        return null;
    }
}

